import * as React from "react";

import { AgentThreadsTable } from "@/Agents/Threads/AgentThreadsTable";
import type { Filter } from "@/shared/components/ResourceView";

interface TriggerThreadsProps {
  triggerId: string;
}

/**
 * "Threads" tab of the trigger detail page: every agent thread this trigger's
 * ``start_agent`` targets launched.
 *
 * The same composition `AgentThreadsPage` uses for an agent, pinned by
 * `created_from_trigger_id` instead of `created_from_agent_id`, so the tab
 * inherits sorting, filtering, and the column picker rather than reimplementing
 * a table. The scope is enforced server-side and survives the user picking OR
 * match mode for their own filters — `composeFilters` wraps the scope and the
 * widget filters in an outer AND.
 *
 * The Trigger column starts hidden: every row would repeat this trigger. The
 * column picker can re-show it.
 *
 * This tab and the Dispatches tab answer different questions, which is why both
 * exist: a dispatch row records that a launch was attempted and how it went,
 * including the failures that produced no thread at all; this lists the threads
 * that resulted, with their titles and status.
 */
export function TriggerThreads({ triggerId }: TriggerThreadsProps) {
  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "created_from_trigger_id",
        comparator: "EQUALS",
        values: [triggerId],
      },
    ],
    [triggerId],
  );

  return (
    <AgentThreadsTable
      scope={scope}
      defaultHiddenColumnIds={["created_from_trigger_id"]}
      noRowsContent="No agent threads launched by this trigger yet."
    />
  );
}
